import React, { useMemo } from "react";
import { LineChartComponent } from "@/components/ui/charts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { useReportingData } from "@/hooks/useReportingData";

export interface AttendanceChartProps {
  className?: string;
  detailed?: boolean;
  dateRange?: { from: Date; to: Date };
}

export function AttendanceChart({ className, detailed = false, dateRange }: AttendanceChartProps) {
  const { attendanceData } = useReportingData(dateRange);

  const avgAttendance = useMemo(() => {
    if (!attendanceData.length) return 0;
    const total = attendanceData.reduce((sum, d) => sum + (d.attendance || 0), 0);
    return Math.round(total / attendanceData.length);
  }, [attendanceData]);
  const percentChange = useMemo(() => {
    if (attendanceData.length < 2) return 0;
    const last = attendanceData[attendanceData.length - 1]?.attendance || 0;
    const prev = attendanceData[attendanceData.length - 2]?.attendance || 0;
    if (prev === 0) return last > 0 ? 100 : 0;
    return last - prev;
  }, [attendanceData]);

  return (
    <Card className={cn("shadow-lg border-0 bg-gradient-to-br from-white via-purple-50 to-purple-100/60 backdrop-blur-md p-0", className)}>
      <CardHeader className="pb-2 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <CardTitle className="text-2xl font-bold text-purple-900 flex items-center gap-2">
            Attendance
            <span className="text-lg font-normal text-purple-500">(%)</span>
          </CardTitle>
          <div className="flex items-baseline gap-4 mt-2">
            <span className="text-3xl font-extrabold text-purple-700">{avgAttendance}%</span>
            <span className={cn(
              "text-sm font-semibold px-2 py-1 rounded-full",
              percentChange > 0 ? "bg-green-100 text-green-700" : percentChange < 0 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"
            )}>
              {percentChange > 0 ? "+" : ""}{percentChange.toFixed(1)}%
            </span>
            <span className="text-xs text-muted-foreground">vs previous period</span>
          </div>
        </div>
        <Calendar className="h-8 w-8 text-purple-400" />
      </CardHeader>
      <CardContent className="pt-0 pb-6">
        <div className="h-80 w-full">
          <LineChartComponent
            data={attendanceData}
            index="month"
            categories={["attendance"]}
            colors={["purple"]}
            valueFormatter={(value) => `${value}%`}
          />
        </div>
      </CardContent>
    </Card>
  );
}
